"use client";
import { useSetAtom } from "jotai";
import { ReactFlowProvider } from "@xyflow/react";
import {
  Account,
  autoLayoutSetter,
  BuiltInApp,
  RawCanvas,
  Session,
  Toggles,
  ZSL,
} from "@zapier/canvas-state";
import { CurrentAccountIdProvider } from "@zapier/identity";
import { useEffect } from "react";

import { Canvas } from "../components/Canvas";
import { CanvasHydrator } from "../components/CanvasHydrator";
import { CanvasBackground } from "../components/CanvasBackground";
import { BuilderNav } from "../components/BuilderNav";
import { EmbedOverlay } from "../components/Canvas/EmbedOverlay";
import useUpdateSelectedApps from "../hooks/useUpdateSelectedApps";

type Props = {
  canvas?: RawCanvas;
  template?: ZSL;
  chatbotsApp: BuiltInApp;
  tablesApp: BuiltInApp;
  interfacesApp: BuiltInApp;
  session?: Session;
  account?: Account;
  toggles: Toggles;
  autoLayoutOnLoad?: boolean;
  isEmbedOverride?: boolean;
  isTemplatePage?: boolean;
};

function getCanvasFromTemplate(template?: ZSL) {
  const templateCanvas = template?.canvases?.[0];

  if (!templateCanvas) return undefined;

  const { name, nodes, edges, description } = templateCanvas;

  return { name, nodes, edges, description };
}

export function IframeEmbeddedCanvas({
  canvas,
  template,
  chatbotsApp,
  tablesApp,
  interfacesApp,
  session,
  account,
  toggles,
  autoLayoutOnLoad,
  isEmbedOverride = false,
  isTemplatePage = false,
}: Props) {
  const isInIframe =
    typeof window !== "undefined" && window.self !== window.top;

  const canvasWithFallback = (canvas ||
    getCanvasFromTemplate(template)) as RawCanvas;

  useUpdateSelectedApps();

  return (
    <CurrentAccountIdProvider>
      <ReactFlowProvider>
        <CanvasHydrator
          canvas={canvasWithFallback}
          template={template}
          chatbotsApp={chatbotsApp}
          tablesApp={tablesApp}
          interfacesApp={interfacesApp}
          session={session}
          account={account}
          toggles={toggles}
          isEmbed={isInIframe || isEmbedOverride}
          isTemplatePage={isTemplatePage}
        >
          <AutoLayoutSideEffect autoLayoutOnLoad={autoLayoutOnLoad} />
          {isInIframe ? (
            <>
              <Page />
              <EmbedOverlay />
            </>
          ) : (
            <BuilderNav>
              <Page />
            </BuilderNav>
          )}
        </CanvasHydrator>
      </ReactFlowProvider>
    </CurrentAccountIdProvider>
  );
}

function Page() {
  return (
    <Canvas>
      <CanvasBackground />
    </Canvas>
  );
}

function AutoLayoutSideEffect({
  autoLayoutOnLoad,
}: {
  autoLayoutOnLoad?: boolean;
}) {
  const setAutoLayout = useSetAtom(autoLayoutSetter);
  useEffect(() => {
    if (autoLayoutOnLoad) {
      setTimeout(() => setAutoLayout());
    }
  }, [autoLayoutOnLoad, setAutoLayout]);

  return null;
}
